import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef } from 'react';

import { getOrvalErrorToastContent, isOrvalNotFoundError } from '@r/api/orval-error-helper.ts';

type OrvalErrorToastOptions = Parameters<typeof toast.error>[1];

interface OrvalQueryErrorToastInput {
  isError: boolean;
  error: unknown;
  errorUpdatedAt?: number;
}

interface OrvalQueryErrorToastConfig {
  skipNotFound?: boolean;
}

export function useOrvalErrorToast() {
  const { t } = useTranslation();

  return useCallback(
    (error: unknown, fallback?: string, options?: OrvalErrorToastOptions) => {
      const { title, titleIsTranslationKey, description } = getOrvalErrorToastContent(
        error,
        fallback
      );
      const translatedTitle = titleIsTranslationKey ? t(title) : title;
      const toastOptions = description ? { ...options, description } : options;

      toast.error(translatedTitle, toastOptions);
    },
    [t]
  );
}

export function useOrvalQueryErrorToast(
  query: OrvalQueryErrorToastInput,
  fallback?: string,
  options?: OrvalErrorToastOptions,
  config?: OrvalQueryErrorToastConfig
) {
  const showOrvalErrorToast = useOrvalErrorToast();
  const lastNotifiedErrorRef = useRef<unknown>(null);
  const lastNotifiedAtRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!query.isError) {
      lastNotifiedErrorRef.current = null;
      lastNotifiedAtRef.current = undefined;
      return;
    }

    if (config?.skipNotFound && isOrvalNotFoundError(query.error)) {
      return;
    }

    const hasSameTimestamp =
      query.errorUpdatedAt !== undefined && query.errorUpdatedAt === lastNotifiedAtRef.current;
    const hasSameError =
      query.errorUpdatedAt === undefined && query.error === lastNotifiedErrorRef.current;

    if (hasSameTimestamp || hasSameError) {
      return;
    }

    lastNotifiedErrorRef.current = query.error;
    lastNotifiedAtRef.current = query.errorUpdatedAt;
    showOrvalErrorToast(query.error, fallback, options);
  }, [
    config?.skipNotFound,
    fallback,
    options,
    query.error,
    query.errorUpdatedAt,
    query.isError,
    showOrvalErrorToast
  ]);
}
